import React, { useEffect } from "react";
import SendTxTemplate from "./components/SendTxTemplate";
import { InputSearch } from "../../../components/InputSearch";
import { formatAmountWithSATS, localeFormatNumber } from "../../../utils/Utils";
import { ButtonVariant } from "../../../config/colors";
import RadioButton from "../../../components/RadioButton";
import { useTransaction } from "../../../hooks/useTransaction";
import { useNavigate } from "react-router-dom";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { getDisplayNameBulkPayment } from "../../../models/BulkPayment";
import { PaymentKind } from "../../../repositories/WalletRepository";
import { APP_NAME } from "../../../config/app";
import UploadTransactionImageButton from "./components/UploadTransactionImageButton";
import { WalletType } from "../../../models/Transactions";
import { OPAAvatar } from "../../../components/OPAAvatar";

interface SendTxRecapProps {

}

const SendTxRecap: React.FC<SendTxRecapProps> = () => {
    const navigate = useNavigate();
    const {
        setTransactionComment, 
        setTransactionAnonymous, 
        scanData, 
        recipient, 
        sendingFees,
        amountIncludesFees,
        amount, 
        comment, 
        imageUrl,
        setTransactionImageUrl,
        anonymous,
        isAmountValid,
        isRecipientValid,
        payTransaction,
        loading,
        error
    } = useTransaction();

    useEffect(() => {
        if(!isRecipientValid)
            navigate('/home/wallet/send/recipient');
        else if(!isAmountValid)
            navigate('/home/wallet/send/amount');
    }, []);

    const onChangeComment = (comment: string) => {
        setTransactionComment(comment);
    }

    const onChangeAnonymously = (anonymous: boolean) => {
        setTransactionAnonymous(anonymous);
    }

    const handleButtonClick = async () => {
        const transaction = await payTransaction();
        if(transaction) {
            switch(transaction.walletType) {
                case WalletType.SPARK:
                    navigate('/home/wallet/tx/' + transaction.id, { state: { template: 'success' } });
                    break;
                case WalletType.INTERNAL:
                    navigate('/home/wallet/tx/' + transaction.paymentHash, { state: { template: 'success' } });
                    break;
            }
        }
    }

    const handleChangeComment = (comment: string) => {
        const maxLength = scanData?.commentAllowed ?? 250;
        const croppedComment = comment.slice(0, maxLength);
        onChangeComment(croppedComment);
    }

    const getValueFieldTo = () => {
        if(recipient?.bulkPayment)
            return getDisplayNameBulkPayment(recipient.bulkPayment);
        else if(recipient?.pleb?.getLabelToShowSendToWallet())
            return recipient?.pleb?.getLabelToShowSendToWallet();
        else if(scanData?.paymentRequest)
            return scanData?.paymentRequest;
        else if(scanData?.lnurl)
            return scanData?.lnurl;
        else if(scanData?.invoice)
            return scanData?.invoice;
        return '';
    }

    const getRecipientPicture = () => {
        return recipient?.pleb?.thumbnail
            || recipient?.pleb?.image
            || undefined;
    }

    const getAmout = () : number => {
        let res = 0;
        if(recipient?.bulkPayment)
            res = recipient.bulkPayment.recipientsCount * (amount ?? 0);
        else
            res = amount ?? 0;

        if(amountIncludesFees)
            res -= sendingFees ?? 0;

        return res;
    }

    const recipientPicture = getRecipientPicture();

    const blocks = [
        <div>
            <InputSearch
                placeholder="To"
                value={getValueFieldTo()}
                readOnly
                elementBefore={
                    recipientPicture ? (
                        <OPAAvatar
                            imageUrl={recipientPicture}
                            size={24}
                            borderWidth={0}
                            borderColor="var(--brand-bg-2)"
                        />
                    ) : undefined
                }
            />
        </div>,
    ];

    if(
        (scanData?.commentAllowed && scanData?.commentAllowed > 0) ||
        recipient?.bulkPayment
    )
        blocks.push(<div>
            <div className="mb-1 flex flex-row justify-between items-center">
                <div className="text-lg font-semibold">Message</div>
                <div className="text-sm text-brand-color-2">
                    {scanData?.commentAllowed && `Max ${scanData.commentAllowed} characters`}
                </div>
            </div>
            <InputSearch
                placeholder="Message"
                value={comment ?? ''}
                onClear={() => handleChangeComment('')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeComment(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                    }
                }}
            />
            {
                (scanData?.walletInternal !== undefined || recipient?.bulkPayment || recipient?.pleb) ? (
                    <UploadTransactionImageButton
                        imageUrl={imageUrl}
                        onImageUploaded={(uploadedImageUrl) => setTransactionImageUrl(uploadedImageUrl)}
                        onImageRemoved={() => setTransactionImageUrl(undefined)}
                    />
                ) : null
            }
        </div>)

    const opaFees = scanData?.opaFeesInPercent ? (getAmout()) * (scanData?.opaFeesInPercent / 100) : 0;
    const sendingFeesValue = sendingFees ?? 0;

    blocks.push(
        <div className="flex flex-col gap-3">
            <div className="flex flex-row justify-between items-center">
                <div className="text-lg font-semibold">Amount</div>
                <div className="text-lg font-semibold">{formatAmountWithSATS(getAmout())}</div>
            </div>
            {
                (opaFees > 0 || sendingFeesValue > 0) && (
                    <>
                        {
                            opaFees > 0 && (
                                <div className="flex flex-row justify-between items-center text-brand-color-2">
                                    <div className="">{APP_NAME} Fees ({(scanData?.opaFeesInPercent ?? 0).toLocaleString(localeFormatNumber)}%)</div>
                                    <div className="">{formatAmountWithSATS(opaFees)}</div>
                                </div>
                            )
                        }
                        {
                            sendingFeesValue > 0 && (
                                <div className="flex flex-row justify-between items-center text-brand-color-2">
                                    <div className="">Fees</div>
                                    <div className="">{formatAmountWithSATS(sendingFeesValue)}</div>
                                </div>
                            )
                        }
                        <div className="flex flex-row justify-between items-center bg-brand-color-2 h-[1px] my-2"></div>
                        <div className="flex flex-row justify-between items-center">
                            <div className="text-lg font-semibold">Total Amount</div>
                            <div className="text-lg font-semibold">{formatAmountWithSATS((getAmout()) + opaFees + sendingFeesValue)}</div>
                        </div>
                    </>
                )
            }
        </div>        
    )

    return (
        <LayoutWithTopBar
            breadcrumbs={[
                { title: 'Wallet', onClick: () => navigate('/home/wallet') },
                { title: 'Send' }
            ]}
            onClickPrevArrow={() => navigate(-1)}
        >
            <SendTxTemplate
                onButtonClick={handleButtonClick}
                buttonDisabled={false}
                buttonLoading={loading}
                blocks={blocks}
                error={loading ? undefined : error}
                buttonVariant={ButtonVariant.primary}
                buttonText={scanData?.kind === PaymentKind.WITHDRAW ? "CLAIM IT" : "SEND IT"}
                afterButton={
                    (scanData?.walletInternal !== undefined || recipient?.bulkPayment || recipient?.pleb) && (
                        <div 
                            className="flex flex-row justify-between items-center cursor-pointer gap-2 py-2" 
                            onClick={() => onChangeAnonymously(!anonymous)}
                        >
                            <div className="uppercase font-medium opacity-80">
                                Send anonymously
                            </div>
                            <RadioButton
                                checked={anonymous ?? false}
                            />
                        </div>
                    )
                }
            >
            </SendTxTemplate>
        </LayoutWithTopBar>
    );
}

export default SendTxRecap;